import React from 'react';
import { Box, Card, CardActionArea, Grid, Typography } from '@mui/material';
import { SvgIconProps } from '@mui/material/SvgIcon';
import { ArrowForwardIos, OpenInNew } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { useL10n } from '@uplift/hooks';
import { useHelpCenterStyles } from './support-page-styles';

export type HelpCenterTile = {
  title: string;
  description: string;
  icon: React.ComponentType<SvgIconProps>;
  href: string;
};

type HelpfulLinksSectionProps = {
  tiles: HelpCenterTile[];
  heading?: string;
  maxColumns?: number;
};

const isExternalHref = (href: string) => /^https?:\/\//i.test(href);

export const HelpfulLinksSection: React.VFC<HelpfulLinksSectionProps> = ({ tiles, heading, maxColumns = 4 }) => {
  const { classes } = useHelpCenterStyles();
  const l10n = useL10n();
  const columns = Math.min(tiles.length, maxColumns) || 1;

  return (
    <Box>
      <Typography variant="h6" color="secondary" className={classes.sectionHeading}>
        {heading ?? l10n.support.helpfulLinksHeading}
      </Typography>
      <Grid container spacing={3}>
        {tiles.map((tile) => {
          const IconComponent = tile.icon;
          const external = isExternalHref(tile.href);

          return (
            <Grid size={{ xs: 12, sm: 6, md: 12 / columns }} key={tile.title}>
              <Card className={classes.tileCard}>
                <CardActionArea
                  {...(external
                    ? {
                      component: 'a' as const,
                      href: tile.href,
                      target: '_blank',
                      rel: 'noopener noreferrer',
                    }
                    : {
                      component: RouterLink,
                      to: tile.href,
                    })}
                  className={classes.tileAction}
                >
                  <Box className={classes.tileIconBox}>
                    <IconComponent color="secondary" fontSize="medium" />
                  </Box>
                  <Typography variant="subtitle1" className={classes.tileTitle}>
                    {tile.title}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" className={classes.tileDescription}>
                    {tile.description}
                  </Typography>
                  <Box className={classes.viewLink}>
                    <Typography variant="body2" color="secondary" component="span">View</Typography>
                    {external && (
                      <OpenInNew fontSize="inherit" color="secondary" className={classes.viewLinkIcon} />
                    )}
                  </Box>
                  <ArrowForwardIos fontSize="small" className={classes.mobileChevron} />
                </CardActionArea>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};
